import React, { useState, useEffect, useRef } from "react";
import PermissionFormItem from "../../../../../components/permissions/PermissionFormItem";
import { Form, DatePicker, TimePicker } from "antd";
import Icon from "../../../../../components/AppIcon";
import { getEmployees } from "../../../../../api/employees";
import dayjs from "dayjs";

const AUTO_LOAN_NUMBER_REGEX = /^LN-\d{4}-\d+$/i;
const isAutoGeneratedLoanNumber = (value) =>
  AUTO_LOAN_NUMBER_REGEX.test(String(value || "").trim());

const FieldShell = ({ label, children }) => (
  <div className="space-y-1">
    <div className="text-[13px] text-muted-foreground">{label}</div>
    {children}
  </div>
);

const setFieldIfChanged = (form, field, nextValue) => {
  const currentValue = form?.getFieldValue?.(field);
  if (String(currentValue ?? "") === String(nextValue ?? "")) return;
  form?.setFieldsValue?.({ [field]: nextValue });
};

const PostFileDispatchAndRecords = ({ form }) => {
  const loanNumber = Form.useWatch("loan_number", form);
  const dispatchDate = Form.useWatch("dispatch_date", form);
  const dispatchTime = Form.useWatch("dispatch_time", form);
  const disbursementDate = Form.useWatch("disbursement_date", form);
  const disbursementTime = Form.useWatch("disbursement_time", form);
  const [employees, setEmployees] = useState([]);
  const [loanNumberInput, setLoanNumberInput] = useState("");
  const loanNumberFocusedRef = useRef(false);
  const hasActualLoanNumber = String(loanNumberInput || "").trim().length > 0;

  const formatDateForInput = (value) => {
    if (!value) return "";
    if (dayjs.isDayjs(value)) return value.format("YYYY-MM-DD");
    if (typeof value === "string") {
      const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) return match[1];
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  };
  const asDayjsDate = (value) => {
    const date = formatDateForInput(value);
    return date ? dayjs(date) : null;
  };
  const asDayjsTime = (value) => {
    const time = formatTimeForInput(value);
    return time ? dayjs(time, "HH:mm") : null;
  };

  const formatTimeForInput = (value) => {
    if (!value) return "";
    if (dayjs.isDayjs(value)) return value.format("HH:mm");
    const text = String(value);
    const match = text.match(/(\d{2}:\d{2})/);
    return match ? match[1] : "";
  };

  // Load autosuggest data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const empData = await getEmployees();
        
        // Employees usually come as array of objects
        setEmployees(empData.map(e => e.name || e.username || e.email)); // Adjust based on API response structure
      } catch (err) {
        console.error("Error fetching master data:", err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const normalizedLoanNumber = isAutoGeneratedLoanNumber(loanNumber)
      ? ""
      : String(loanNumber || "");

    if (
      isAutoGeneratedLoanNumber(loanNumber) &&
      String(loanNumber || "").trim()
    ) {
      setFieldIfChanged(form, "loan_number", "");
    }

    if (loanNumberFocusedRef.current) return;
    setLoanNumberInput((prev) =>
      prev === normalizedLoanNumber ? prev : normalizedLoanNumber
    );
  }, [loanNumber, form]);

  const handleLoanNumberChange = (e) => {
    const nextValue = e.target.value;
    setLoanNumberInput(nextValue);
    setFieldIfChanged(form, "loan_number", nextValue);
  };

  return (
    <div className="space-y-4 md:space-y-5">
      <PermissionFormItem name="loan_number" hidden><input /></PermissionFormItem>
      <PermissionFormItem name="dispatch_date" hidden><input /></PermissionFormItem>
      <PermissionFormItem name="dispatch_time" hidden><input /></PermissionFormItem>
      <PermissionFormItem name="disbursement_date" hidden><input /></PermissionFormItem>
      <PermissionFormItem name="disbursement_time" hidden><input /></PermissionFormItem>

      <div className="rounded-2xl border border-border bg-[#f9fafb] dark:bg-slate-950/35 p-4 md:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              Post-File Operations
            </div>
            <h2 className="mt-1 text-lg font-semibold text-foreground">
              Dispatch & Record Details
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Capture dispatch movement, disbursement timing and bank record details.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground">
            <Icon name="Clock3" size={13} className="text-primary" />
            Timeline critical
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-4 md:p-5">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Send" size={15} className="text-primary" />
              </div>
              <div className="text-sm font-semibold text-foreground">Dispatch Movement</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FieldShell label="Dispatch Date">
                <PermissionFormItem className="mb-0">
                  <DatePicker
                    className="w-full"
                    format="DD MMM YYYY"
                    value={asDayjsDate(dispatchDate)}
                    onChange={(date) =>
                      setFieldIfChanged(
                        form,
                        "dispatch_date",
                        date ? date.format("YYYY-MM-DD") : "",
                      )
                    }
                  />
                </PermissionFormItem>
              </FieldShell>

              <FieldShell label="Dispatch Time">
                <PermissionFormItem className="mb-0">
                  <TimePicker
                    className="w-full"
                    format="HH:mm"
                    value={asDayjsTime(dispatchTime)}
                    onChange={(time) =>
                      setFieldIfChanged(
                        form,
                        "dispatch_time",
                        time ? time.format("HH:mm") : "",
                      )
                    }
                  />
                </PermissionFormItem>
              </FieldShell>

              <FieldShell label="Dispatch Through">
                <PermissionFormItem name="dispatch_through" className="mb-0">
                  <input type="text" className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background" placeholder="e.g., Courier" />
                </PermissionFormItem>
              </FieldShell>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 md:p-5">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="CreditCard" size={15} className="text-primary" />
              </div>
              <div className="text-sm font-semibold text-foreground">Disbursement & Bank Record</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FieldShell label="Disbursement Date">
                <PermissionFormItem className="mb-0">
                  <DatePicker
                    className="w-full"
                    format="DD MMM YYYY"
                    value={asDayjsDate(disbursementDate)}
                    onChange={(date) =>
                      setFieldIfChanged(
                        form,
                        "disbursement_date",
                        date ? date.format("YYYY-MM-DD") : "",
                      )
                    }
                  />
                </PermissionFormItem>
              </FieldShell>

              <FieldShell label="Disbursement Time">
                <PermissionFormItem className="mb-0">
                  <TimePicker
                    className="w-full"
                    format="HH:mm"
                    value={asDayjsTime(disbursementTime)}
                    onChange={(time) =>
                      setFieldIfChanged(
                        form,
                        "disbursement_time",
                        time ? time.format("HH:mm") : "",
                      )
                    }
                  />
                </PermissionFormItem>
              </FieldShell>

              <FieldShell label="Loan Number">
                <PermissionFormItem className="mb-0">
                  <div className="relative">
                    <input
                      type="text"
                      className={`w-full border rounded-md px-3 py-2 text-sm font-semibold transition-all ${
                        hasActualLoanNumber
                          ? "border-primary bg-primary/12 text-primary ring-2 ring-primary/30 shadow-[0_0_0_1px_rgba(59,130,246,0.12)]"
                          : "border-border bg-background text-foreground"
                      }`}
                      placeholder="Enter Loan Number..."
                      value={loanNumberInput}
                      onFocus={() => {
                        loanNumberFocusedRef.current = true;
                      }}
                      onBlur={() => {
                        loanNumberFocusedRef.current = false;
                      }}
                      onChange={handleLoanNumberChange}
                    />
                    <div
                      className={`absolute right-2 top-1/2 -translate-y-1/2 ${hasActualLoanNumber ? "text-primary" : "text-muted-foreground"}`}
                    >
                      <Icon
                        name={hasActualLoanNumber ? "CheckCircle" : "AlertCircle"}
                        size={14}
                      />
                    </div>
                  </div>
                  {hasActualLoanNumber && (
                    <div className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                      <Icon name="Sparkles" size={11} />
                      Active loan number
                    </div>
                  )}
                </PermissionFormItem>
              </FieldShell>
            </div>
          </div>

          <div className="p-3 bg-primary/5 rounded-md border border-primary/20">
            <p className="text-xs text-foreground flex items-center gap-2">
              <Icon name="Info" size={14} className="text-primary" />
              <span>Auto-generated LN values are placeholders until actual Loan Number is entered.</span>
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 md:p-5">
        <div className="mb-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="FileCheck" size={15} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Record Details</h3>
            <p className="text-xs text-muted-foreground">Document preparation information</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldShell label="Docs Prepared By">
            <EmployeeAutosuggest
              label={null}
              name="docs_prepared_by"
              form={form}
              employees={employees}
            />
          </FieldShell>
        </div>
      </div>
    </div>
  );
};

const EmployeeAutosuggest = ({ label, name, form, employees }) => {
  const [inputValue, setInputValue] = useState(form.getFieldValue(name) || "");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filtered = (employees || []).filter((emp) =>
    emp && emp.toLowerCase().includes((inputValue || "").toLowerCase())
  );

  const handleSelect = (val) => {
    setInputValue(val);
    setFieldIfChanged(form, name, val);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      {label && <label className="text-[13px] text-muted-foreground mb-1 block">{label}</label>}
      <div className="relative">
        <input
          type="text"
          className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background pr-8"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setFieldIfChanged(form, name, e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Select Employee..."
        />
        <Icon name="User" size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        
        {showSuggestions && filtered.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-40 overflow-y-auto z-10">
            {filtered.map((emp, i) => (
              <div key={i} className="px-3 py-2 text-sm hover:bg-muted cursor-pointer" onClick={() => handleSelect(emp)}>
                {emp}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(PostFileDispatchAndRecords);
